import { createFileRoute, Link } from '@tanstack/react-router'
import { CheckCircle, Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { CheckoutLayout } from '../../components/checkout/CheckoutLayout'
import { api } from '../../core/trpc'
import { pushDataLayer } from '../../utils/gtm'
import { trackMetaPurchase } from '../../utils/metaPixel'

const successSearchSchema = z.object({
    transactionId: z.string().optional(),
})

const formatPrice = (price: number) => {
    return (
        new Intl.NumberFormat('hu-HU', {
            style: 'decimal',
            maximumFractionDigits: 0,
        }).format(price) + ' Ft'
    )
}

function CheckoutSuccessPage() {
    const { transactionId } = Route.useSearch()
    const { t } = useTranslation()

    const { data: transaction, isLoading } = api.webCheckout.getTransaction.useQuery(
        { transactionId: transactionId ?? '' },
        { enabled: !!transactionId },
    )

    const totalPrice = transaction?.transactionItems.reduce((sum, item) => sum + item.price, 0) ?? 0

    /**
     * A free registration was never a payment, so it is not announced as one and no invoice is on its
     * way. Read off the loaded transaction rather than a search param, so a shared or reloaded link
     * cannot claim the wrong thing.
     */
    const isFreeRegistration = Boolean(transaction) && totalPrice === 0
    const formatPriceOrFree = (price: number) =>
        price === 0 ? t('common.ui.freeOfCharge') : formatPrice(price)

    const trackedTransactionId = useRef<string | null>(null)
    useEffect(() => {
        if (!transaction || !transactionId) return
        if (trackedTransactionId.current === transactionId) return
        trackedTransactionId.current = transactionId
        trackMetaPurchase(totalPrice)
        pushDataLayer({
            event: 'purchase',
            ecommerce: {
                transaction_id: transactionId,
                value: totalPrice,
                currency: 'HUF',
                items: transaction.transactionItems.map((item) => ({
                    item_name: item.name,
                    price: item.price,
                    quantity: 1,
                })),
            },
        })
    }, [transaction, transactionId, totalPrice])

    return (
        <CheckoutLayout>
            <div className="mx-auto max-w-[600px] px-6 py-12">
                <div className="flex flex-col items-center justify-center gap-6 rounded-[2rem] border border-primary/10 bg-white p-12 shadow-xl shadow-primary/5">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                        <CheckCircle className="h-10 w-10 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-brand-dark">
                        {isFreeRegistration
                            ? t('frontend.checkout.successfulRegistration')
                            : 'Sikeres fizetés!'}
                    </h2>

                    {isLoading && <Loader2 className="h-6 w-6 animate-spin text-brand-gray" />}

                    {transaction && (
                        <div className="w-full">
                            <div className="rounded-2xl bg-slate-50 px-6 py-5">
                                <h3 className="mb-3 text-sm font-semibold text-brand-dark">
                                    {isFreeRegistration
                                        ? 'Jelentkezés részletei'
                                        : 'Rendelés részletei'}
                                </h3>
                                {transaction.transactionItems.map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between py-1 text-sm"
                                    >
                                        <span className="text-brand-dark">{item.name}</span>
                                        <span className="font-semibold text-brand-dark">
                                            {formatPriceOrFree(item.price)}
                                        </span>
                                    </div>
                                ))}
                                <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3">
                                    <span className="text-sm font-semibold text-brand-dark">
                                        Összesen:
                                    </span>
                                    <span className="text-lg font-bold text-primary">
                                        {formatPriceOrFree(totalPrice)}
                                    </span>
                                </div>
                            </div>

                            {transaction.billingEmail &&
                                (isFreeRegistration ? (
                                    <p className="mt-4 text-center text-sm text-brand-gray">
                                        {t('frontend.checkout.confirmationSentTo', {
                                            email: transaction.billingEmail,
                                        })}
                                    </p>
                                ) : (
                                    <p className="mt-4 text-center text-sm text-brand-gray">
                                        A számlát a(z){' '}
                                        <span className="font-semibold">
                                            {transaction.billingEmail}
                                        </span>{' '}
                                        címre küldjük el.
                                    </p>
                                ))}
                        </div>
                    )}

                    {!transaction && !isLoading && (
                        <p className="text-brand-gray">
                            Köszönjük a vásárlást! A számlát e-mailben küldjük el Önnek.
                        </p>
                    )}

                    <Link
                        to="/"
                        className="mt-4 rounded-2xl bg-primary px-8 py-4 text-base font-bold text-white shadow-xl shadow-primary/20 transition-all hover:bg-primary/90"
                    >
                        Vissza a főoldalra
                    </Link>
                </div>
            </div>
        </CheckoutLayout>
    )
}

export const Route = createFileRoute('/checkout/success')({
    head: () => ({
        meta: [{ title: 'Long-Y - Sikeres fizetés' }],
    }),
    component: CheckoutSuccessPage,
    validateSearch: successSearchSchema,
})
